/**
 * English UI catalogue of the curve editor (W2-04b curve-editor), namespace 'curveEditor'.
 * Imports only src/i18n/define.ts; zh-CN lives in src/i18n/zh-CN/curveEditor.ts (W3-09).
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('curveEditor', {
  // Column and header
  columnTitle: 'Curve editor',
  channelColumnTitle: 'Channel view',
  headerTitle: '{prop} · {line}',
  pairName: '{pair, select, position {Position X and Y} other {Size W and H}}',
  headerRegion: 'Curve editor: {prop} of {line}',
  lineFallback: 'Line {index}',
  propName:
    '{prop, select, moveX {Position X} moveY {Position Y} rotate {Rotation} alpha {Opacity} speed {Scroll speed} scaleX {Length / Size X} scaleY {Thickness / Size Y} color {Colour} text {Text} incline {Note tilt} paint {Brush} gif {GIF progress} other {{prop}}}',
  propNameNormal: '{prop, select, scaleX {Length / Size X} scaleY {Thickness / Size Y} other {{prop}}}',
  layersLabel: 'Layers',
  // Layer 0 is "L0" everywhere (UX-039: one name, the same as the motion column chips). Unused in the views now
  // (they use layerChipShort) but kept so the zh-CN catalogue entry stays valid.
  layerChipBase: 'L0',
  layerChipShort: 'L{n}',
  layerChipHint:
    '{name}: {state, select, keys {has keyframes} other {no keyframes}}{muted, select, yes {, muted in the preview} other {}}. Click to edit this layer.',
  layerMuted: 'Muted in the preview (export unchanged)',
  sigmaLane: 'Sum of layers',
  sigmaLaneHint: 'What Phira plays: every layer added together. Read-only.',
  flattenButton: 'Flatten to layer 0',
  flattenNothing: 'Only one layer: there is nothing to flatten.',
  onlyOneLayer: 'This property has one layer, so there is nothing to add up.',
  channelShowsAll: 'The channel view already shows every layer and their sum.',
  flattenHint: 'Bakes the sum of all layers into layer 0 and removes the other layers.',
  selectionNone: 'Click a keyframe or a curve to select it',
  selectionKeys: '{count, plural, one {# keyframe selected} other {# keyframes selected}}',
  selectionSegments: '{count, plural, one {# segment selected} other {# segments selected}}',
  selectionBoth:
    '{keys, plural, one {# keyframe} other {# keyframes}} and {segments, plural, one {# segment} other {# segments}} selected',
  toolbarLabel: 'Curve editor tools',
  actionsLabel: 'Selected segment actions',

  // Commands
  cmdToggle: 'Expand or collapse the curve editor',
  cmdExpand: 'Open the curve editor',
  cmdCollapse: 'Close the curve editor',
  cmdChannelView: 'Channel view: all layers side by side',
  cmdSigma: 'Show the sum of layers',
  cmdGhosts: 'Show the other layers faintly',
  cmdDistance: 'Show the distance travelled (Scroll speed)',
  cmdFitValues: 'Fit the value axis to the visible curve',
  cmdZoomValuesIn: 'Zoom the value axis in',
  cmdZoomValuesOut: 'Zoom the value axis out',
  cmdFlatten: 'Flatten layers to layer 0',
  cmdMakeBezier: 'Turn into a custom curve (Bezier)',
  cmdEditBezier: 'Edit the custom curve',
  cmdResetSlice: 'Use the whole curve',
  cmdAddLayer: 'Add a layer to this property',
  cmdLayer: 'Edit another layer in the curve editor',
  argLayer: 'Layer number',
  cmdReverse: 'Reverse segments',
  cmdLink: 'Link segments',
  cmdDeleteKeys: 'Delete keyframes',

  // Header buttons (selected segments and keys)
  btnChangeCurve: 'Change curve…',
  btnHold: 'Stay, then jump',
  btnJump: 'Make jump',
  btnRemoveJump: 'Continue from previous',
  btnBake: 'Bake to steps…',
  btnReverse: 'Reverse',
  btnLink: 'Link',
  btnAddLayer: 'Add layer',

  // Disabled reasons and toasts
  noFocusedColumn: 'Click a motion column first',
  noProject: 'Open a project first',
  noCurveEditor: 'The curve editor is not open',
  notNumeric: '{prop} is not a number, so it has no curve. Edit its keyframes in the motion column and the inspector.',
  lineMissing: 'The line of this column no longer exists',
  lineLocked: 'This line is locked. Unlock it in the Lines panel to edit it.',
  channelNeedsLayers:
    'Channel view is for properties that add up layers: Position X, Position Y, Rotation, Opacity and Scroll speed.',
  channelFullOnly: 'Layers are a Full-level feature. Switch to Full to see the channel view.',
  speedOnly: 'Only the Scroll speed property has a distance-travelled curve.',
  noSegments: 'Select one or more segments first',
  noKeys: 'Select one or more keyframes first',
  noJumpKeys: 'Select a keyframe with a jump first',
  allJump: 'The selected keyframes already jump',
  firstKeyJump: 'The first keyframe of a curve cannot jump: nothing arrives at it.',
  toolMissing: 'This tool is not available',
  noBezierSegment: 'Select a segment with a custom curve',
  noTweenSegments: 'Select a segment that uses a numbered curve',
  noSlicedSegments: 'Select a segment that uses part of a numbered curve',
  flatSegment:
    'This segment starts and ends at the same value, so its custom curve cannot be shaped. Move a keyframe first.',
  sliceRefused: 'Phira cannot use this part of the curve. Keep dragging to a part that rises or falls.',
  layerLimit: 'A property can have at most 4 layers in the editor',
  bezierFitted: 'Custom curve fitted to the original (largest difference {percent} %)',
  bezierNone: 'Custom curves are not available for {prop}',
  arriveFixed:
    'After a Stay segment the keyframe arrives at the stayed value. Drag up or down to move it in time, or drag the right half to change the value it leaves from.',

  // Transaction labels
  txMoveKeys: 'Move {count, plural, one {# keyframe} other {# keyframes}}',
  txMoveSegment: 'Move segment',
  txBezier: 'Shape custom curve',
  txSlice: 'Change the part of the curve',
  txAddKey: 'Add keyframe',
  txChangeCurve: 'Change curve',
  txMakeBezier: 'Make custom curve',
  txResetSlice: 'Use the whole curve',
  txAddLayer: 'Add layer',
  txHold: 'Stay, then jump',
  txJump: 'Make jump',
  txRemoveJump: 'Continue from previous',
  txDeleteKeys: 'Delete {count, plural, one {# keyframe} other {# keyframes}}',

  // Canvas readouts
  readoutValue: '{beat} = {value}',
  readoutJump: '{beat} = {vin} then {vout}',
  readoutBezier: 'handle {handle}: time {x}, value {y}',
  readoutSlice: 'part {left} to {right}',
  crossScreen: 'Notes cross the screen in {sec} s',
  crossNever: 'Notes do not move up here (speed 0 or less)',
  emptyTrack: '{prop} has no keyframes yet. Double-click to add the first keyframe.',
  emptyLayer: 'This layer has no keyframes. Double-click to add one.',
  readOnlySum: 'Read-only: edit the layers on the left.',
  noEngine: 'Open a project to see its curves.',
  distanceTick: '{value} px',
  curveLinear: 'Linear',
  curveHold: 'Stay',
  curveBezier: 'Custom',
  curveGuided: '{strength}, {shape}',
  curveFull: '{name} ({id})',
  curvePart: '{name}, part',
  curveBaked: '{name} (baked on export)',
  curveIgnored: '{name} (Phira plays it linear)',
  easeStrength:
    '{s, select, gentle {Gentle} soft {Soft} medium {Medium} strong {Strong} stronger {Stronger} extreme {Extreme} sharp {Sharp} overshoot {Overshoot} springy {Springy} bouncy {Bouncy} other {{s}}}',
  easeShape: '{s, select, slowStart {slow start} slowEnd {slow end} slowBoth {slow both} other {{s}}}',
  unitPx: '{v} px',
  unitDeg: '{v}°',
  unitPercent: '{v} %',
  unitTimes: '×{v}',
  unitPlain: '{v}',

  // Gesture hints (hint bar)
  hintAxisLock: 'Lock to one direction',
  hintRound: 'Round values',
  hintNoSnap: 'No snapping',
  hintCancel: 'Cancel',

  // Settings rows
  settingSigma: 'Curve editor: show the sum of layers',
  settingSigmaHelp: 'Draws the value Phira plays (all layers added) as a dashed line over the layer you edit.',
  settingGhosts: 'Curve editor: show the other layers',
  settingGhostsHelp: 'Draws the curves of the other layers faintly behind the one you edit.',
  settingDistance: 'Curve editor: show the distance travelled for Scroll speed',
  settingDistanceHelp:
    'Adds the floor position (how far the notes have travelled) as a dashed curve with its own scale along the bottom.',
  settingChannel: 'Curve editor: open in channel view',
  settingChannelHelp: 'Shows every layer of the property side by side, with the read-only sum at the right.',

  // Help
  helpTitle: 'The curve editor',
  helpBody:
    'Press G with a motion column focused (or double-click its header) to open a large graph of that property. Time runs up like the note lane, the value runs left to right.\n\nDrag a keyframe to move it: up and down changes its time (it snaps to the beat grid), left and right changes its value. Hold Ctrl for round values, Alt to turn snapping off, Shift to lock the direction you start dragging in. Keyframes never pass each other.\n\nA keyframe with a jump shows two halves: drag the left half to change the value it arrives at and the right half for the value it leaves from. After a Stay segment the keyframe arrives at the stayed value, so its faint left half only moves it in time.\n\nRight-click a keyframe or a curve for the same actions as the bar above the graph: change the curve, Stay then jump, make or remove a jump, bake, reverse, link or delete.\n\nCustom curves show two round handles; drag them to shape the curve. Curves that use part of a numbered curve show a small rail beside the segment with two square handles that pick the part.\n\nDouble-click empty space to add a keyframe without changing the motion, and double-click a curve to pick another one.\n\nIn Guided, Position opens with X and Y side by side (and Size with W and H on image lines). The channel view shows every layer of the property side by side and the sum Phira plays; "Flatten to layer 0" merges the layers. For Scroll speed the thick curve is the speed that really moves the notes, the dashed curve is the distance travelled, and the readout at the playhead tells how long notes take to cross the screen.',
});

export default catalogOf(m);
